import React, { useState, useEffect, useRef, useCallback } from 'react';
import './IframeContainer.css';
import { createHostChromeInsetRelay } from '../utils/hostChromeInset';

const getUrlPreview = (value) => {
  if (!value || typeof value !== 'string') return value;
  return value.length > 120 ? `${value.substring(0, 120)}...` : value;
};

const isValidIframeUrl = (value) => {
  if (!value || typeof value !== 'string') return false;
  try {
    const parsedUrl = new URL(value);
    return parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * IframeContainer - A component that displays external content in a nested iframe
 * while keeping the parent sidebar React app mounted
 * 
 * @param {string} url - The URL to load in the nested iframe
 * @param {string} title - The title for accessibility
 * @param {Function} onLoadError - Callback for iframe navigation failures
 */
const IframeContainer = ({ url, title, onLoadError, loadTimeoutMs = 30000 }) => {
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);
  const dragOverCountRef = useRef(0);
  const navigationIdRef = useRef(0);
  const onLoadErrorRef = useRef(onLoadError);

  // Track previous URL to detect changes
  const prevUrlRef = useRef(url);

  // Passes the height of the extension panel's window controls down to the nested app,
  // so it can keep that band free at its top (see utils/hostChromeInset.js).
  const hostChromeInsetRelayRef = useRef(null);
  if (!hostChromeInsetRelayRef.current) {
    hostChromeInsetRelayRef.current = createHostChromeInsetRelay({
      parentWindow: window.parent !== window ? window.parent : null,
      getChildWindow: () => iframeRef.current?.contentWindow || null,
    });
  }

  useEffect(() => {
    const relay = hostChromeInsetRelayRef.current;
    const handleMessage = (event) => relay.handleMessage(event);
    window.addEventListener('message', handleMessage);
    // The extension may have sent its value before this container mounted.
    relay.requestFromParent();
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    onLoadErrorRef.current = onLoadError;
  }, [onLoadError]);

  const reportLoadError = useCallback((reason, details = {}) => {
    const payload = {
      reason,
      url,
      title,
      navigationId: navigationIdRef.current,
      ...details,
    };
    console.warn('[IframeContainer] iframe load issue', {
      ...payload,
      url: getUrlPreview(payload.url),
    });
    onLoadErrorRef.current?.(payload);
  }, [title, url]);

  // Force iframe navigation when URL changes
  useEffect(() => {
    const prevUrl = prevUrlRef.current;

    if (!isValidIframeUrl(url)) {
      setIsLoading(false);
      reportLoadError('invalid-url', { previousUrl: prevUrl });
      prevUrlRef.current = url;
      return;
    }

    if (url && prevUrl !== url) {
      navigationIdRef.current += 1;
      console.log('[IframeContainer] URL changed - starting navigation', {
        navigationId: navigationIdRef.current,
        from: getUrlPreview(prevUrl),
        to: getUrlPreview(url),
      });
      setIsLoading(true);
    }
    prevUrlRef.current = url;
  }, [reportLoadError, url]);

  useEffect(() => {
    if (!isLoading || !isValidIframeUrl(url) || !loadTimeoutMs) return undefined;

    const timeoutNavigationId = navigationIdRef.current;
    const timeoutId = window.setTimeout(() => {
      if (navigationIdRef.current !== timeoutNavigationId) return;
      setIsLoading(false);
      reportLoadError('timeout', { timeoutMs: loadTimeoutMs });
    }, loadTimeoutMs);

    return () => window.clearTimeout(timeoutId);
  }, [isLoading, loadTimeoutMs, reportLoadError, url]);

  const handleIframeLoad = () => {
    console.log('[IframeContainer] onLoad fired - clearing loading state', {
      navigationId: navigationIdRef.current,
      url: getUrlPreview(url),
    });
    setIsLoading(false);
    hostChromeInsetRelayRef.current?.onChildLoad();
  };

  const handleIframeError = () => {
    console.error('[IframeContainer] onError fired - iframe failed to load');
    setIsLoading(false);
    reportLoadError('error-event');
  };

  /**
   * Serializes File objects to a format that can be sent via postMessage
   * and reconstructed in the iframe
   */
  const serializeFiles = async (files) => {
    const filePromises = Array.from(files).map(async (file) => {
      const arrayBuffer = await file.arrayBuffer();
      return {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
        arrayBuffer: Array.from(new Uint8Array(arrayBuffer)), // Convert to regular array for JSON
      };
    });
    return await Promise.all(filePromises);
  };

  /**
   * Sends a message to the iframe via postMessage
   */
  const sendMessageToIframe = (type, data) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type, data }, '*');
    }
  };

  // Listen for messages from nested iframe (client app) and relay to grandparent (chrome extension)
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (data && data.key === 'nested-iframe-url-update') {
        // Relay URL updates to grandparent (chrome extension)
        console.log("[BACK-TO-LOAN] IframeContainer relaying sidebar-url-update to parent:", data.value);
        window?.parent?.postMessage({ key: 'sidebar-url-update', value: data.value }, '*');
      }
      if (data && data.key === 'has-announcement-banner') {
        // Relay announcement banner visibility to grandparent (chrome extension)
        // This allows the extension to hide its close/expand icons when the banner is visible
        window?.parent?.postMessage({ key: 'has-announcement-banner', value: data.value }, '*');
      }
      if (data && data.key === 'selected-org-branding') {
        // Relay selected-organization branding from the nested client app to the
        // grandparent (chrome extension) so the floating launcher re-brands when
        // the user switches organizations inside the sidebar.
        window?.parent?.postMessage({ key: 'selected-org-branding', value: data.value }, '*');
      }
      // Relay login-related messages from nested iframe to Chat.js (parent)
      // These messages are sent by the Login component in the client app when shown in an iframe
      if (data && data.key === 'needs-addy-login') {
        // User needs to log in - relay to Chat.js so it can handle the login flow
        console.log('[IframeContainer] Received needs-addy-login message, relaying to parent');
        window?.parent?.postMessage({ key: 'needs-addy-login', value: data.value }, '*');
      }
      if (data && data.key === 'addy-login-success') {
        // User successfully logged in - relay to Chat.js so it can reload the iframe
        console.log('[IframeContainer] Received addy-login-success message, relaying to parent');
        window?.parent?.postMessage({ key: 'addy-login-success', value: data.value }, '*');
      }
      if (data && data.key === 'addy-cookies-blocked') {
        // The browser is blocking cookies for the nested client app, so login
        // can never complete in the panel. Relay so the extension can help.
        console.log('[IframeContainer] Received addy-cookies-blocked message, relaying to parent');
        window?.parent?.postMessage(
          { key: 'addy-cookies-blocked', value: data.value, status: data.status },
          '*'
        );
      }
      if (data && data.key === 'addy-open-cookie-settings') {
        // Only the extension can open the browser's cookie settings page.
        console.log('[IframeContainer] Received addy-open-cookie-settings message, relaying to parent');
        window?.parent?.postMessage({ key: 'addy-open-cookie-settings', value: true }, '*');
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Check if this is a file drag
      if (e.dataTransfer?.types?.includes('Files')) {
        dragOverCountRef.current++;
        sendMessageToIframe('PARENT_DRAG_ENTER', { hasFiles: true });
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (e.dataTransfer?.types?.includes('Files')) {
        sendMessageToIframe('PARENT_DRAG_ENTER', { hasFiles: true });
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      dragOverCountRef.current--;
      
      // Only send drag leave when we've actually left the document
      if (dragOverCountRef.current <= 0) {
        dragOverCountRef.current = 0;
        sendMessageToIframe('PARENT_DRAG_LEAVE', {});
      }
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      dragOverCountRef.current = 0;
      
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        try {
          // Serialize files and send to iframe
          const serializedFiles = await serializeFiles(files);
          sendMessageToIframe('PARENT_DROP', { files: serializedFiles });
        } catch (error) {
          console.error('❌ [IframeContainer] Error serializing files:', error);
        }
      }
    };

    // Add event listeners to the container div (parent window)
    const container = document.querySelector('.iframe-container');
    if (container) {
      container.addEventListener('dragenter', handleDragEnter);
      container.addEventListener('dragover', handleDragOver);
      container.addEventListener('dragleave', handleDragLeave);
      container.addEventListener('drop', handleDrop);
    }

    // Also listen on document as fallback
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);

    return () => {
      if (container) {
        container.removeEventListener('dragenter', handleDragEnter);
        container.removeEventListener('dragover', handleDragOver);
        container.removeEventListener('dragleave', handleDragLeave);
        container.removeEventListener('drop', handleDrop);
      }
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, [isLoading]); // Re-run when iframe loads

  return (
    <div className="iframe-container">
      {/* Loading indicator */}
      {isLoading && (
        <div className="iframe-loading">
          <div className="iframe-spinner"></div>
          <p>Loading...</p>
        </div>
      )}
      
      {/* Nested iframe - displays external content */}
      <iframe
        ref={iframeRef}
        id="nested-content-iframe"
        src={url}
        className="nested-iframe"
        title={title}
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads allow-storage-access-by-user-activation"
        allow="clipboard-read; clipboard-write"
        onLoad={handleIframeLoad}
        onError={handleIframeError}
      />
    </div>
  );
};

export default IframeContainer;

